import { useEffect, useRef, useState } from 'react';
import type { AlertsConfig, WebhookDeliveryStatus, WebhookMethod } from '@kkp-fraud/shared';
import { webhookUsage } from '../alerts';
import { ApiError, COOKIE_MODE, webhooksApi } from '../api';
import { ActionMenu } from '../components/ActionMenu';
import { useShell } from '../components/AppLayout';
import { Dialog } from '../components/Dialog';
import { Field, useFieldErrors } from '../components/Field';
import { Tip } from '../components/PageHead';
import { DEMO } from '../demo';
import { isDemoWebhookId } from '../demo/webhooks';
import { t } from '../i18n';
import { When } from '../components/When';
import { canEdit } from '../session';
import {
  WEBHOOK_AUTH_TYPES,
  WEBHOOK_DELIVERY_STATUSES,
  WEBHOOK_METHODS,
  WEBHOOK_PROBLEM_KEY,
  WEBHOOK_TEMPLATE_VARIABLES,
  emptyForm,
  formOf,
  shortUrl,
  unknownVariables,
  webhookErrorKey,
  webhookProblems,
  writeOf,
  type DeliveryFilters,
  type WebhookAuthType,
  type WebhookDelivery,
  type WebhookForm,
  type WebhookPreview,
  type WebhookProblem,
  type WebhookTestResult,
  type WebhookView,
} from '../webhooks';

/**
 * Alert Management → Webhooks (R3): the connections an alert rule's `webhook` channel calls,
 * edited like a Postman request — method and URL on one bar, then Headers / Auth / Body (with a
 * variable picker and a live preview of the sample decision) / Settings, "Send test" with the
 * answer, and each connection's delivery log.
 *
 * Connections live in the admin service, not in the rule document (their headers carry
 * secrets): saving one takes effect at once, with no publish. Secrets are write-only — the
 * service answers "set" and never the value; an empty box keeps what is stored. Everybody
 * signed in reads; Editor and Approver write and test.
 */

const MASK = '••••';
type EditorTab = 'headers' | 'auth' | 'body' | 'settings';

export function webhookErrorText(e: unknown): string {
  if (e instanceof ApiError) {
    const key = webhookErrorKey(e.code);
    if (key) return t(key, { detail: e.message });
    if (e.problems?.length) return e.problems.map((p) => `${p.field}: ${p.message}`).join('; ');
  }
  return (e as Error).message;
}

const DELIVERY_CLASS: Record<WebhookDeliveryStatus, string> = { sent: 'chip ok', failed: 'chip bad', retrying: 'chip warn' };

function DeliveryStatusChip({ status }: { status: WebhookDeliveryStatus }) {
  return <span className={DELIVERY_CLASS[status]}>{t(`webhooks.delivery.status.${status}`)}</span>;
}

// ---- the test result -----------------------------------------------------------------------

function TestResultPanel({ result }: { result: WebhookTestResult }) {
  return (
    <section className="stack" data-testid="webhook-test-result" aria-label={t('webhooks.test.result.title')}>
      <div className="row-wrap" style={{ gap: 8, alignItems: 'center' }}>
        <span className={result.ok ? 'chip ok' : 'chip bad'} data-testid="webhook-test-verdict">
          {t(result.ok ? 'webhooks.test.ok' : 'webhooks.test.failed')}
        </span>
        <span className="small mono">{t('webhooks.test.http', { status: result.httpStatus ?? '—', ms: result.durationMs })}</span>
        {result.error && <span className="small mono muted">{result.error}</span>}
      </div>
      <div className="small muted">{t('webhooks.test.response')}</div>
      <pre className="wh-pre" data-testid="webhook-test-response">
        {result.responsePreview ?? '—'}
      </pre>
      {result.requestPreview && (
        <>
          <div className="small muted">{t('webhooks.test.request')}</div>
          <pre className="wh-pre">
            {`${result.requestPreview.method} ${result.requestPreview.url}\n${Object.entries(result.requestPreview.headers)
              .map(([k, v]) => `${k}: ${v}`)
              .join('\n')}\n\n${result.requestPreview.body}`}
          </pre>
        </>
      )}
    </section>
  );
}

// ---- the editor --------------------------------------------------------------------------------

/** QA-A-11: `enabled` false for a read-only caller — the admin service's preview route is for writers, so it is not asked. */
function useLivePreview(form: WebhookForm, enabled: boolean): { preview: WebhookPreview | null; failed: boolean } {
  const { settings } = useShell();
  const [preview, setPreview] = useState<WebhookPreview | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!COOKIE_MODE || !enabled) return;
    const ctl = new AbortController();
    const timer = setTimeout(() => {
      webhooksApi
        .preview(settings, { bodyTemplate: form.bodyTemplate, contentType: form.contentType, method: form.method }, ctl.signal)
        .then((p) => {
          setPreview(p);
          setFailed(false);
        })
        .catch(() => !ctl.signal.aborted && setFailed(true));
    }, 250);
    return () => {
      clearTimeout(timer);
      ctl.abort();
    };
  }, [settings, enabled, form.bodyTemplate, form.contentType, form.method]);
  return { preview, failed };
}

/** Which editor tab holds the control a problem belongs to; absent = the always-visible top of the dialog. */
const TAB_OF: Partial<Record<WebhookProblem, EditorTab>> = {
  header_name: 'headers',
  header_reserved: 'headers',
  header_duplicate: 'headers',
  header_secret_missing: 'headers',
  auth_secret_missing: 'auth',
  auth_header_name: 'auth',
  content_type: 'body',
  unknown_variable: 'body',
  timeout: 'settings',
  attempts: 'settings',
  backoff: 'settings',
};

const HEADER_PROBLEMS: readonly WebhookProblem[] = ['header_name', 'header_reserved', 'header_duplicate', 'header_secret_missing'];

/** One header row's own problems — so the box that is wrong can be marked (and focused), not just the table. */
function headerRowProblems(form: WebhookForm, i: number, isNew: boolean): { name: boolean; value: boolean } {
  const h = form.headers[i]!;
  const own = webhookProblems({ ...form, headers: [h] }, isNew).filter((p) => HEADER_PROBLEMS.includes(p));
  const key = h.name.trim().toLowerCase();
  const duplicate =
    own.length === 0 &&
    key !== '' &&
    form.headers.slice(0, i).some((o) => !(o.name.trim() === '' && o.value === '') && o.name.trim().toLowerCase() === key);
  return {
    name: duplicate || own.includes('header_name') || own.includes('header_reserved'),
    value: own.includes('header_secret_missing'),
  };
}

function WebhookEditor({
  initial,
  usedBy,
  mayWrite,
  startDelete = false,
  onClose,
  onSaved,
  onDeleted,
}: {
  initial: WebhookView | null;
  usedBy: readonly string[];
  mayWrite: boolean;
  /** Opened from the row's Delete: the dialog starts at its own delete confirmation. */
  startDelete?: boolean;
  onClose: () => void;
  onSaved: (w: WebhookView) => void;
  onDeleted: (id: string) => void;
}) {
  const { settings, showToast } = useShell();
  const isNew = initial === null;
  const [saved, setSaved] = useState<WebhookView | null>(initial);
  const [form, setForm] = useState<WebhookForm>(() => (initial ? formOf(initial) : emptyForm()));
  const [tab, setTab] = useState<EditorTab>('body');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<WebhookTestResult | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(startDelete && initial !== null && usedBy.length === 0);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const { preview, failed } = useLivePreview(form, mayWrite);
  const fields = useFieldErrors();

  const problems = webhookProblems(form, saved === null);
  const patch = (p: Partial<WebhookForm>) => setForm((f) => ({ ...f, ...p }));
  const setHeader = (i: number, p: Partial<WebhookForm['headers'][number]>) =>
    setForm((f) => ({ ...f, headers: f.headers.map((h, j) => (j === i ? { ...h, ...p } : h)) }));
  const readOnly = !mayWrite;
  const dirty = saved === null || JSON.stringify(writeOf(form, false)) !== JSON.stringify(writeOf(formOf(saved), false));

  const unknown = unknownVariables(form.bodyTemplate);
  const says = (p: WebhookProblem) => (problems.includes(p) ? t(WEBHOOK_PROBLEM_KEY[p], { names: unknown.join(', ') }) : undefined);
  // UX review P2-5: an error shows after its field was left or Save was pressed — never on a
  // fresh dialog — and never to someone who cannot change the connection.
  const show = <T,>(name: string, err: T | undefined): T | undefined => (mayWrite ? fields.show(name, err) : undefined);
  const kept = form.storedAuth.type === form.authType ? form.storedAuth : { token: false, user: false, password: false, value: false };
  const secretPlaceholder = (stored: boolean) => (stored ? t('webhooks.secret.set') : '');
  const authMissing = says('auth_secret_missing');
  const headerError = show('headers', HEADER_PROBLEMS.map(says).filter(Boolean).join('; ') || undefined);

  const insertVariable = (name: string) => {
    const token = `{{${name}}}`;
    const el = bodyRef.current;
    const at = el ? el.selectionStart : form.bodyTemplate.length;
    const end = el ? el.selectionEnd : at;
    patch({ bodyTemplate: `${form.bodyTemplate.slice(0, at)}${token}${form.bodyTemplate.slice(end)}` });
  };

  const save = async () => {
    if (busy) return;
    const ok = problems.length === 0;
    if (!ok) {
      // A problem on a tab that is not open: open the first such tab, so there is something to see and focus.
      const onScreen = problems.some((p) => TAB_OF[p] === undefined || TAB_OF[p] === tab);
      const elsewhere = problems.map((p) => TAB_OF[p]).find((x) => x !== undefined);
      if (!onScreen && elsewhere) setTab(elsewhere);
    }
    if (!fields.attempt(ok, document.querySelector('[data-testid="webhook-editor"]'))) return;
    setBusy(true);
    setError(null);
    try {
      const body = writeOf(form, saved === null);
      const w = saved === null ? await webhooksApi.create(settings, body) : await webhooksApi.update(settings, saved.id, body, saved.etag);
      setSaved(w);
      setForm(formOf(w));
      showToast(t('webhooks.saved.toast', { id: w.id }));
      onSaved(w);
    } catch (e) {
      setError(webhookErrorText(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!saved) return;
    setBusy(true);
    setError(null);
    try {
      await webhooksApi.remove(settings, saved.id, saved.etag);
      showToast(t('webhooks.deleted.toast', { id: saved.id }));
      onDeleted(saved.id);
    } catch (e) {
      setError(webhookErrorText(e));
      setConfirmDelete(false);
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    if (!saved) return;
    setTesting(true);
    setError(null);
    setResult(null);
    try {
      setResult(await webhooksApi.test(settings, saved.id));
    } catch (e) {
      setError(webhookErrorText(e));
    } finally {
      setTesting(false);
    }
  };

  /** A header box marked wrong while the headers' error shows (it names what is wrong). */
  const headerBox = (bad: boolean) =>
    bad && headerError ? { 'aria-invalid': true as const, 'aria-describedby': 'wh-headers-err', className: 'mono-input invalid' } : { className: 'mono-input' };

  return (
    <Dialog
      busy={busy}
      wide
      title={isNew && saved === null ? t('webhooks.new.title') : t('webhooks.edit.title', { id: saved?.id ?? '' })}
      testid="webhook-editor"
      onClose={onClose}
      foot={
        <>
          {saved && mayWrite && !confirmDelete && (
            <>
              <button type="button" className="btn danger" disabled={busy || usedBy.length > 0} onClick={() => setConfirmDelete(true)}>
                {t('webhooks.delete.action')}
              </button>
              {usedBy.length > 0 && <Tip>{t('webhooks.delete.blocked.tip', { ids: usedBy.join(', ') })}</Tip>}
            </>
          )}
          {confirmDelete && (
            <>
              <span className="small">{t('webhooks.delete.confirm', { id: saved?.id ?? '' })}</span>
              <button type="button" className="btn danger-solid" data-testid="webhook-delete-confirm" disabled={busy} onClick={() => void remove()}>
                {t('webhooks.delete.action')}
              </button>
            </>
          )}
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {t(mayWrite ? 'btn.cancel.label' : 'btn.close.label')}
          </button>
          {mayWrite && (
            <button type="button" className="btn primary" data-testid="webhook-save" disabled={busy} onClick={() => void save()}>
              {t('webhooks.save.action')}
            </button>
          )}
        </>
      }
    >
      {/* The Postman bar: method + URL, and Send test on the saved connection. */}
      <div className="wh-bar">
        <div style={{ width: 110, alignSelf: 'flex-start' }}>
          <Field label={t('webhooks.method.label')}>
            <select value={form.method} disabled={readOnly} onChange={(e) => patch({ method: e.target.value as WebhookMethod })}>
              {WEBHOOK_METHODS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div style={{ flex: 1, alignSelf: 'flex-start' }}>
          <Field label={t('webhooks.url.label')} required={mayWrite} name="url" form={fields} error={show('url', says('url'))}>
            <input type="text" spellCheck={false} readOnly={readOnly} className="mono-input" value={form.url} onChange={(e) => patch({ url: e.target.value })} />
          </Field>
          {/* QA-A-19: an incoming-webhook URL carries its token — a read-only role sees the host only. */}
          {saved?.urlRedacted && (
            <div className="small muted" data-testid="webhook-url-redacted">
              {t('webhooks.url.redacted')}
            </div>
          )}
        </div>
        {saved && mayWrite && (
          <button type="button" className="btn" data-testid="webhook-send-test" disabled={testing || !COOKIE_MODE || (DEMO && isDemoWebhookId(saved.id))} onClick={() => void sendTest()}>
            {testing ? t('webhooks.test.sending') : t('webhooks.test.action')}
          </button>
        )}
        {saved && mayWrite && dirty && <Tip align="right">{t('webhooks.test.saved_only.tip')}</Tip>}
      </div>

      <div className="form-grid">
        <Field label={t('webhooks.id.label')} required={mayWrite && saved === null} name="id" form={fields} error={show('id', says('id'))}>
          <input type="text" className="mono-input" value={form.id} readOnly={saved !== null} onChange={(e) => patch({ id: e.target.value.toLowerCase() })} />
        </Field>
        <div style={{ gridColumn: 'span 2' }}>
          <Field label={t('webhooks.name.label')} required={mayWrite} name="name" form={fields} error={show('name', says('name'))}>
            <input type="text" readOnly={readOnly} value={form.name} onChange={(e) => patch({ name: e.target.value })} />
          </Field>
        </div>
        <Field label={t('webhooks.enabled.label')}>
          <input type="checkbox" role="switch" className="toggle" disabled={readOnly} checked={form.enabled} onChange={(e) => patch({ enabled: e.target.checked })} />
        </Field>
      </div>

      <nav className="tabs" role="tablist" aria-label={t('webhooks.editor.tabs')}>
        {(['headers', 'auth', 'body', 'settings'] as const).map((id) => (
          <button key={id} type="button" role="tab" className="tab" aria-selected={tab === id} onClick={() => setTab(id)}>
            {t(`webhooks.tab.${id}`)}
            {id === 'headers' && form.headers.length > 0 && <span className="count">{form.headers.length}</span>}
          </button>
        ))}
      </nav>

      {tab === 'headers' && (
        <section className="stack" data-testid="webhook-headers">
          <table>
            <thead>
              <tr>
                <th>{t('webhooks.header.name')}</th>
                <th>{t('webhooks.header.value')}</th>
                <th style={{ width: 90 }}>{t('webhooks.header.secret')}</th>
                <th style={{ width: 40 }} aria-label={t('webhooks.header.remove.a11y')} />
              </tr>
            </thead>
            <tbody>
              {form.headers.length === 0 && (
                <tr>
                  <td colSpan={4} className="empty">
                    {t('webhooks.headers.empty')}
                  </td>
                </tr>
              )}
              {form.headers.map((h, i) => {
                const bad = headerRowProblems(form, i, saved === null);
                return (
                  <tr key={i}>
                    <td>
                      <input
                        type="text"
                        {...headerBox(bad.name)}
                        aria-label={t('webhooks.header.name')}
                        readOnly={readOnly}
                        value={h.name}
                        onBlur={() => fields.leave('headers')}
                        onChange={(e) => {
                          fields.edit('headers');
                          setHeader(i, { name: e.target.value });
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type={h.secret ? 'password' : 'text'}
                        {...headerBox(bad.value)}
                        aria-label={t('webhooks.header.value')}
                        autoComplete="off"
                        readOnly={readOnly}
                        placeholder={h.secret ? secretPlaceholder(h.stored) : ''}
                        value={h.value}
                        onBlur={() => fields.leave('headers')}
                        onChange={(e) => {
                          fields.edit('headers');
                          setHeader(i, { value: e.target.value });
                        }}
                      />
                    </td>
                    <td>
                      <input type="checkbox" aria-label={t('webhooks.header.secret')} disabled={readOnly || (h.stored && h.secret)} checked={h.secret} onChange={(e) => setHeader(i, { secret: e.target.checked })} />
                    </td>
                    <td>
                      {!readOnly && (
                        <button
                          type="button"
                          className="btn ghost small"
                          aria-label={t('webhooks.header.remove.a11y')}
                          onClick={() => setForm((f) => ({ ...f, headers: f.headers.filter((_, j) => j !== i) }))}
                        >
                          ✕
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {headerError && (
            <span className="field-error" id="wh-headers-err" data-testid="webhook-headers-error">
              {headerError}
            </span>
          )}
          {!readOnly && (
            <div>
              <button type="button" className="btn small" data-testid="webhook-header-add" onClick={() => patch({ headers: [...form.headers, { name: '', value: '', secret: false, stored: false }] })}>
                {t('webhooks.header.add')}
              </button>
            </div>
          )}
        </section>
      )}

      {tab === 'auth' && (
        <section className="form-grid" data-testid="webhook-auth">
          <Field label={t('webhooks.auth.type')}>
            <select value={form.authType} disabled={readOnly} onChange={(e) => patch({ authType: e.target.value as WebhookAuthType })}>
              {WEBHOOK_AUTH_TYPES.map((a) => (
                <option key={a} value={a}>
                  {t(`webhooks.auth.${a}`)}
                </option>
              ))}
            </select>
          </Field>
          {form.authType === 'bearer' && (
            <div style={{ gridColumn: 'span 2' }}>
              <Field
                label={t('webhooks.auth.token')}
                required={mayWrite && !kept.token}
                name="token"
                form={fields}
                error={show('token', form.token === '' && !kept.token ? authMissing : undefined)}
              >
                <input type="password" autoComplete="off" readOnly={readOnly} placeholder={secretPlaceholder(kept.token)} value={form.token} onChange={(e) => patch({ token: e.target.value })} />
              </Field>
            </div>
          )}
          {form.authType === 'basic' && (
            <>
              <Field
                label={t('webhooks.auth.user')}
                required={mayWrite && !kept.user}
                name="user"
                form={fields}
                error={show('user', form.user === '' && !kept.user ? authMissing : undefined)}
              >
                <input type="password" autoComplete="off" readOnly={readOnly} placeholder={secretPlaceholder(kept.user)} value={form.user} onChange={(e) => patch({ user: e.target.value })} />
              </Field>
              <Field
                label={t('webhooks.auth.password')}
                required={mayWrite && !kept.password}
                name="password"
                form={fields}
                error={show('password', form.password === '' && !kept.password ? authMissing : undefined)}
              >
                <input type="password" autoComplete="off" readOnly={readOnly} placeholder={secretPlaceholder(kept.password)} value={form.password} onChange={(e) => patch({ password: e.target.value })} />
              </Field>
            </>
          )}
          {form.authType === 'header' && (
            <>
              <Field label={t('webhooks.auth.header_name')} required={mayWrite} name="authHeaderName" form={fields} error={show('authHeaderName', says('auth_header_name'))}>
                <input type="text" className="mono-input" readOnly={readOnly} value={form.authHeaderName} onChange={(e) => patch({ authHeaderName: e.target.value })} />
              </Field>
              <Field
                label={t('webhooks.auth.header_value')}
                required={mayWrite && !kept.value}
                name="authHeaderValue"
                form={fields}
                error={show('authHeaderValue', form.authHeaderValue === '' && !kept.value ? authMissing : undefined)}
              >
                <input type="password" autoComplete="off" readOnly={readOnly} placeholder={secretPlaceholder(kept.value)} value={form.authHeaderValue} onChange={(e) => patch({ authHeaderValue: e.target.value })} />
              </Field>
            </>
          )}
          <div className="small muted" style={{ gridColumn: '1 / -1' }}>
            {t('webhooks.auth.write_only')}
          </div>
        </section>
      )}

      {tab === 'body' && (
        <section className="stack" data-testid="webhook-body">
          {form.method === 'GET' && <div className="banner warn">{t('webhooks.body.get')}</div>}
          <Field label={t('webhooks.content_type.label')} required={mayWrite} name="contentType" form={fields} error={show('contentType', says('content_type'))}>
            <input type="text" className="mono-input" readOnly={readOnly} value={form.contentType} onChange={(e) => patch({ contentType: e.target.value })} />
          </Field>
          {!readOnly && (
            <fieldset className="checkset" data-testid="webhook-variables">
              <legend>
                {t('webhooks.variables.label')} <Tip>{t('webhooks.variables.tip')}</Tip>
              </legend>
              <div className="row-wrap" style={{ gap: 4 }}>
                {WEBHOOK_TEMPLATE_VARIABLES.map((v) => (
                  <button key={v} type="button" className="btn small mono" onClick={() => insertVariable(v)}>
                    {v}
                  </button>
                ))}
              </div>
            </fieldset>
          )}
          <Field label={t('webhooks.body.label')} name="body" form={fields} error={show('body', says('unknown_variable'))}>
            <textarea
              ref={bodyRef}
              rows={9}
              spellCheck={false}
              readOnly={readOnly}
              className="mono-input"
              value={form.bodyTemplate}
              onChange={(e) => patch({ bodyTemplate: e.target.value })}
            />
          </Field>
          {/* QA-A-11: the preview renders a draft for whoever may change it; a read-only role reads the body above. */}
          {!readOnly && (
            <>
              <div className="small muted">
                {t('webhooks.preview.label')}
                {preview?.jsonValid === false && (
                  <span className="chip warn" style={{ marginLeft: 6 }} data-testid="webhook-preview-json">
                    {t('webhooks.preview.not_json')}
                  </span>
                )}
              </div>
              <pre className="wh-pre" data-testid="webhook-preview">
                {!COOKIE_MODE || failed ? t('webhooks.preview.unavailable') : (preview?.body ?? '…')}
              </pre>
            </>
          )}
        </section>
      )}

      {tab === 'settings' && (
        <section className="form-grid" data-testid="webhook-settings">
          <Field label={t('webhooks.timeout.label')} required={mayWrite} name="timeout" form={fields} error={show('timeout', says('timeout'))}>
            <input
              type="number"
              min={100}
              max={10000}
              readOnly={readOnly}
              value={Number.isFinite(form.timeoutMs) ? form.timeoutMs : ''}
              onChange={(e) => patch({ timeoutMs: e.target.value === '' ? Number.NaN : Number(e.target.value) })}
            />
          </Field>
          <Field label={t('webhooks.attempts.label')} hint={t('webhooks.attempts.tip')} required={mayWrite} name="attempts" form={fields} error={show('attempts', says('attempts'))}>
            <input
              type="number"
              min={1}
              max={5}
              readOnly={readOnly}
              value={Number.isFinite(form.attempts) ? form.attempts : ''}
              onChange={(e) => patch({ attempts: e.target.value === '' ? Number.NaN : Number(e.target.value) })}
            />
          </Field>
          <Field label={t('webhooks.backoff.label')} required={mayWrite} name="backoff" form={fields} error={show('backoff', says('backoff'))}>
            <input
              type="number"
              min={0}
              max={60000}
              readOnly={readOnly}
              value={Number.isFinite(form.backoffMs) ? form.backoffMs : ''}
              onChange={(e) => patch({ backoffMs: e.target.value === '' ? Number.NaN : Number(e.target.value) })}
            />
          </Field>
        </section>
      )}

      {error && (
        <div className="banner error" role="alert" data-testid="webhook-error">
          {error}
        </div>
      )}
      {result && <TestResultPanel result={result} />}
    </Dialog>
  );
}

// ---- the delivery log ---------------------------------------------------------------------------

function DeliveriesDialog({ webhook, onClose }: { webhook: WebhookView; onClose: () => void }) {
  const { settings } = useShell();
  const [filters, setFilters] = useState<DeliveryFilters>({});
  const [rows, setRows] = useState<WebhookDelivery[] | null>(null);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [source, setSource] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = async (f: DeliveryFilters, after?: string) => {
    try {
      const page = await webhooksApi.deliveries(settings, webhook.id, f, after);
      setRows((prev) => (after ? [...(prev ?? []), ...page.items] : page.items));
      setCursor(page.nextCursor);
      setSource(page.source);
      setError(null);
    } catch (e) {
      if (!after) setRows([]);
      setError(webhookErrorText(e));
    }
  };

  useEffect(() => {
    setRows(null);
    void load(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, webhook.id]);

  return (
    <Dialog
      wide
      title={t('webhooks.deliveries.title', { id: webhook.id })}
      testid="webhook-deliveries"
      onClose={onClose}
      foot={
        <>
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {t('btn.close.label')}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <label className="field">
          {t('webhooks.deliveries.filter.status')}
          <select
            value={filters.status ?? ''}
            onChange={(e) => setFilters((f) => ({ ...f, status: (e.target.value || undefined) as WebhookDeliveryStatus | undefined }))}
          >
            <option value="">{t('webhooks.deliveries.filter.all')}</option>
            {WEBHOOK_DELIVERY_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`webhooks.delivery.status.${s}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          {t('webhooks.deliveries.filter.kind')}
          <select
            value={filters.test === undefined ? '' : filters.test ? 'test' : 'alert'}
            onChange={(e) => setFilters((f) => ({ ...f, test: e.target.value === '' ? undefined : e.target.value === 'test' }))}
          >
            <option value="">{t('webhooks.deliveries.filter.all')}</option>
            <option value="alert">{t('webhooks.deliveries.kind.alert')}</option>
            <option value="test">{t('webhooks.deliveries.kind.test')}</option>
          </select>
        </label>
      </div>
      {source === 'none' && <div className="banner warn">{t('webhooks.deliveries.no_dispatcher')}</div>}
      {error && (
        <div className="banner error" role="alert">
          {error}
        </div>
      )}
      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th style={{ width: 130 }}>{t('webhooks.deliveries.col.time')}</th>
              <th style={{ width: 90 }}>{t('col.status.label')}</th>
              <th style={{ width: 60 }}>{t('webhooks.deliveries.col.attempt')}</th>
              <th style={{ width: 60 }}>{t('webhooks.deliveries.col.http')}</th>
              <th style={{ width: 70 }}>{t('webhooks.deliveries.col.duration')}</th>
              <th>{t('webhooks.deliveries.col.alert')}</th>
              <th>{t('webhooks.deliveries.col.error')}</th>
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr>
                <td colSpan={7} className="empty">
                  {t('webhooks.loading')}
                </td>
              </tr>
            )}
            {rows?.length === 0 && (
              <tr>
                <td colSpan={7} className="empty" role="status" data-testid="webhook-deliveries-empty">
                  {t('webhooks.deliveries.empty')}
                </td>
              </tr>
            )}
            {rows?.map((d) => (
              <DeliveryRow key={d.id} d={d} open={open === d.id} onToggle={() => setOpen((o) => (o === d.id ? null : d.id))} />
            ))}
          </tbody>
        </table>
      </div>
      {cursor && (
        <div>
          <button type="button" className="btn small" onClick={() => void load(filters, cursor)}>
            {t('webhooks.deliveries.more')}
          </button>
        </div>
      )}
    </Dialog>
  );
}

function DeliveryRow({ d, open, onToggle }: { d: WebhookDelivery; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr className="clickable" data-testid={`delivery-${d.id}`} onClick={onToggle}>
        <td className="small mono">
          <When iso={d.utc} />
        </td>
        <td>
          <DeliveryStatusChip status={d.status} />
        </td>
        <td className="mono">{d.attempt}</td>
        <td className="mono">{d.httpStatus ?? '—'}</td>
        <td className="mono small">{d.durationMs}</td>
        <td className="small">
          <span className="mono">{d.alertRuleId}</span>
          {d.test && (
            <span className="chip info" style={{ marginLeft: 6 }}>
              {t('webhooks.deliveries.kind.test')}
            </span>
          )}
          <div className="muted mono">{d.transactionId}</div>
        </td>
        <td className="small mono">{d.error ?? ''}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7}>
            <div className="small muted">{t('webhooks.test.request')}</div>
            <pre className="wh-pre">
              {d.requestPreview
                ? `${d.requestPreview.method} ${d.requestPreview.url}\n${Object.entries(d.requestPreview.headers)
                    .map(([k, v]) => `${k}: ${v}`)
                    .join('\n')}\n\n${d.requestPreview.body}`
                : '—'}
            </pre>
            <div className="small muted">{t('webhooks.test.response')}</div>
            <pre className="wh-pre">{d.responsePreview ?? '—'}</pre>
          </td>
        </tr>
      )}
    </>
  );
}

// ---- the panel ---------------------------------------------------------------------------------

export function WebhooksPanel({
  config,
  webhooks,
  error,
  onChanged,
}: {
  config: AlertsConfig;
  /** null = loading. */
  webhooks: WebhookView[] | null;
  error: string | null;
  onChanged: () => void;
}) {
  const { session } = useShell();
  const [editing, setEditing] = useState<{ webhook: WebhookView | null; deleting?: boolean } | null>(null);
  const [logOf, setLogOf] = useState<WebhookView | null>(null);
  const mayWrite = COOKIE_MODE && canEdit(session);
  const usage = webhookUsage(config);
  const writable = (w: WebhookView) => mayWrite && !(DEMO && isDemoWebhookId(w.id));

  if (!COOKIE_MODE) {
    return (
      <section className="card">
        <header className="card-head">
          <h2>{t('webhooks.title')}</h2>
        </header>
        <div className="banner warn" role="status" data-testid="webhooks-unavailable" style={{ margin: 12 }}>
          {t('webhooks.unavailable')}
        </div>
      </section>
    );
  }

  return (
    <section className="card">
      <header className="card-head">
        <h2>{t('webhooks.title')}</h2>
        {webhooks && (
          <span className="chip mute" data-testid="webhooks-count">
            {t('webhooks.count', { n: webhooks.length, on: webhooks.filter((w) => w.enabled).length })}
          </span>
        )}
        <Tip>{t('webhooks.lead.tip')}</Tip>
        <span className="spacer" />
        {mayWrite && (
          <button type="button" className="btn small" data-testid="webhook-add" onClick={() => setEditing({ webhook: null })}>
            {t('webhooks.add.action')}
          </button>
        )}
      </header>
      {error && (
        <div className="banner error" role="alert" data-testid="webhooks-error" style={{ margin: 12 }}>
          {error}
        </div>
      )}
      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th style={{ width: 170 }}>{t('webhooks.id.label')}</th>
              <th>{t('webhooks.name.label')}</th>
              <th style={{ width: 110 }}>{t('webhooks.auth.type')}</th>
              <th style={{ width: 150 }}>{t('webhooks.col.used')}</th>
              <th style={{ width: 90 }}>{t('col.status.label')}</th>
              <th style={{ width: 110 }} aria-label={t('webhooks.deliveries.action')} />
              {mayWrite && <th style={{ width: 44 }} aria-label={t('alert.col.actions')} />}
            </tr>
          </thead>
          <tbody>
            {webhooks === null && (
              <tr>
                <td colSpan={mayWrite ? 7 : 6} className="empty">
                  {t('webhooks.loading')}
                </td>
              </tr>
            )}
            {webhooks?.length === 0 && (
              <tr>
                <td colSpan={mayWrite ? 7 : 6} className="empty" role="status" data-testid="webhooks-empty">
                  {t('webhooks.empty')}
                </td>
              </tr>
            )}
            {webhooks?.map((w) => {
              const used = usage.get(w.id) ?? [];
              return (
                <tr key={w.id} data-testid={`webhook-${w.id}`} className="clickable" onClick={() => setEditing({ webhook: w })}>
                  <td className="mono">
                    {w.id}
                    {DEMO && isDemoWebhookId(w.id) && (
                      <span className="chip warn" style={{ marginLeft: 6 }}>
                        {t('master.demo.chip')}
                      </span>
                    )}
                  </td>
                  <td>
                    {/* UX review P1-5: the name is the row's keyboard way in (Enter opens). */}
                    <div>
                      <button
                        type="button"
                        className="rowlink"
                        data-testid={`webhook-open-${w.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditing({ webhook: w });
                        }}
                      >
                        {w.name || w.id}
                      </button>
                    </div>
                    <div className="small muted mono" data-testid={`webhook-url-${w.id}`}>
                      {w.method} {shortUrl(w.url)}
                    </div>
                  </td>
                  <td className="small">
                    {t(`webhooks.auth.${w.auth.type}`)}
                    {w.headers.some((h) => h.secret) && <span className="muted"> · {MASK}</span>}
                  </td>
                  <td>
                    {used.length === 0 ? (
                      <span className="muted">—</span>
                    ) : (
                      <div className="row-wrap">
                        {used.map((id) => (
                          <span key={id} className="chip mono">
                            {id}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={w.enabled ? 'chip ok' : 'chip mute'} data-testid={`webhook-state-${w.id}`}>
                      {t(w.enabled ? 'state.enabled.label' : 'webhooks.state.off')}
                    </span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button type="button" className="btn small" data-testid={`webhook-log-${w.id}`} onClick={() => setLogOf(w)}>
                      {t('webhooks.deliveries.action')}
                    </button>
                  </td>
                  {/* UX review P2-10: Edit first, Delete last and set apart — it opens the dialog's own confirmation. */}
                  {mayWrite && (
                    <td onClick={(e) => e.stopPropagation()}>
                      {writable(w) && (
                        <ActionMenu
                          variant="row"
                          label={t('row.menu.a11y', { id: w.id })}
                          items={[
                            { label: t('row.edit.label'), onSelect: () => setEditing({ webhook: w }) },
                            { label: t('webhooks.delete.action'), onSelect: () => setEditing({ webhook: w, deleting: true }), disabled: used.length > 0, separated: true },
                          ]}
                        />
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <WebhookEditor
          initial={editing.webhook}
          usedBy={editing.webhook ? (usage.get(editing.webhook.id) ?? []) : []}
          mayWrite={editing.webhook ? writable(editing.webhook) : mayWrite}
          startDelete={editing.deleting}
          onClose={() => setEditing(null)}
          onSaved={() => onChanged()}
          onDeleted={() => {
            setEditing(null);
            onChanged();
          }}
        />
      )}
      {logOf && <DeliveriesDialog webhook={logOf} onClose={() => setLogOf(null)} />}
    </section>
  );
}
